import CloseIcon from "@mui/icons-material/Close";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import Brightness5Icon from "@mui/icons-material/Brightness5";
import BedtimeIcon from "@mui/icons-material/Bedtime";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormGroup,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
} from '@mui/material';
import TextField from '../SanitizedTextField/SanitizedTextField';
import PropTypes from "prop-types";
import { isShiftSelected } from "../../utils/workforceShiftIds";

const fieldLabelSx = {
  fontWeight: 600,
  fontSize: "0.75rem",
  color: "#475569",
  mb: 0.75,
  display: "block",
  textTransform: "uppercase",
  letterSpacing: "0.03em",
};

const editableInputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    background: "#fff",
    "& fieldset": {
      borderColor: "#cbd5e1",
    },
    "&:hover fieldset": {
      borderColor: "#94a3b8",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#006ad1",
    },
  },
};

const COMPANY_OPTIONS = [
  { value: "10", label: "Select Company" },
  { value: "11", label: "LESCO" },
  { value: "12", label: "GEPCO" },
  { value: "13", label: "FESCO" },
  { value: "14", label: "IESCO" },
  { value: "15", label: "MEPCO" },
  { value: "26", label: "PESCO" },
  { value: "37", label: "HESCO" },
  { value: "38", label: "SEPCO" },
  { value: "48", label: "QESCO" },
  { value: "59", label: "TESCO" },
];

function ShiftIcon({ shiftName }) {
  const lower = shiftName.toLowerCase();
  if (lower.includes("mor")) {
    return <WbSunnyIcon fontSize="small" />;
  }
  if (lower.includes("eve")) {
    return <Brightness5Icon fontSize="small" />;
  }
  return <BedtimeIcon fontSize="small" />;
}

function FieldError({ message }) {
  if (!message) {
    return null;
  }
  return (
    <FormHelperText error sx={{ mx: 0, mt: 0.5 }}>
      {message}
    </FormHelperText>
  );
}

function FormSelect({ label, error, children, ...selectProps }) {
  return (
    <Grid item xs={12} sm={6}>
      <Typography sx={fieldLabelSx}>{label}</Typography>
      <select className="lineman-form-dialog__select" {...selectProps}>
        {children}
      </select>
      <FieldError message={error} />
    </Grid>
  );
}

export default function AddWorkforceDialog({
  open,
  onClose,
  formik,
  apiError,
  code,
  hierarchy,
  selectedCompanyCode,
  onCompanyChange,
  circleCode,
  circleCodee,
  onCircleChange,
  selectedDivision,
  onDivisionChange,
  filteredCircles,
  filteredDivisions,
  filteredSubDivisions,
  onSubDivisionChange,
  designations,
  shifts,
  selectedShiftIds,
  onShiftToggle,
  showPassword,
  showConfirmPassword,
  onTogglePassword,
  onToggleConfirmPassword,
}) {
  const showError = (name) =>
    formik.touched[name] && formik.errors[name] ? formik.errors[name] : "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="lineman-alert-popups lineman-form-dialog lineman-form-dialog--reference"
      PaperProps={{
        className: "lineman-form-dialog__paper",
      }}
    >
      <DialogTitle
        component="div"
        className="dialog-title lineman-form-dialog__title"
      >
        <Typography component="h2" className="lineman-form-dialog__heading">
          Add WorkForce
        </Typography>
        <IconButton
          id="clearPopup"
          aria-label="Close"
          onClick={onClose}
          size="small"
          className="lineman-form-dialog__close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className="lineman-form-dialog__content">
        {apiError ? (
          <Alert severity="error" className="lineman-form-dialog__alert">
            {apiError}
          </Alert>
        ) : null}

        <Grid container spacing={2.5} className="lineman-form-dialog__grid">
          {code == 10 && (
            <FormSelect
              label="Company"
              value={selectedCompanyCode}
              onChange={onCompanyChange}
            >
              {COMPANY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </FormSelect>
          )}

          {hierarchy <= 2 && (
            <FormSelect
              label="Circle"
              value={circleCodee || ""}
              onChange={onCircleChange}
            >
              <option value="">All Circle</option>
              {filteredCircles.map((circle) => (
                <option key={circle.circleCode} value={circle.circleCode}>
                  {circle.circleName}
                </option>
              ))}
            </FormSelect>
          )}

          {hierarchy <= 3 && (
            <FormSelect
              label="Division"
              value={selectedDivision || ""}
              onChange={onDivisionChange}
              disabled={!circleCode}
            >
              <option value="">All Division</option>
              {filteredDivisions.map((division) => (
                <option
                  key={division.divisionCode}
                  value={division.divisionCode}
                >
                  {division.divisionName}
                </option>
              ))}
            </FormSelect>
          )}

          {hierarchy <= 4 && (
            <FormSelect
              label="Sub-Division"
              name="iv_subDivision"
              value={formik.values.iv_subDivision}
              onChange={onSubDivisionChange}
              onBlur={formik.handleBlur}
              disabled={!selectedDivision}
              error={showError("iv_subDivision")}
            >
              <option value="">All Sub-Division</option>
              {filteredSubDivisions.map((subDivision) => (
                <option
                  key={subDivision.subDivisionCode}
                  value={subDivision.subDivisionCode}
                >
                  {subDivision.subDivisionName}
                </option>
              ))}
            </FormSelect>
          )}

          <FormSelect
            label="Designation"
            name="iv_designation"
            value={formik.values.iv_designation}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={showError("iv_designation")}
          >
            <option value="">Select Type</option>
            {designations.map((item) => (
              <option
                key={item.employeeDesignationId}
                value={item.employeeDesignationId}
              >
                {item.employeeDesignationName}
              </option>
            ))}
          </FormSelect>

          <Grid item xs={12} sm={6}>
            <Typography sx={fieldLabelSx}>Name</Typography>
            <TextField
              fullWidth
              size="small"
              name="iv_name"
              value={formik.values.iv_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(showError("iv_name"))}
              helperText={showError("iv_name")}
              className="lineman-form-dialog__input"
              sx={editableInputSx}
            />
          </Grid>

          <Grid item xs={12} className="lineman-form-dialog__shift-col">
            <Typography sx={fieldLabelSx}>Shift</Typography>
            <FormGroup className="lineman-form-dialog__shift-group lineman-form-dialog__shift-group--inline">
              {shifts.map((item) => (
                <Box
                  key={item.shiftId}
                  component="label"
                  className={`lineman-form-dialog__shift-option lineman-form-dialog__shift-option--card ${
                    isShiftSelected(selectedShiftIds, item.shiftId)
                      ? "selected"
                      : ""
                  }`}
                >
                  <Checkbox
                    size="small"
                    checked={isShiftSelected(selectedShiftIds, item.shiftId)}
                    onChange={() => onShiftToggle(item.shiftId)}
                    sx={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
                  />
                  <Box className="shift-icon" aria-hidden>
                    <ShiftIcon shiftName={item.shiftName} />
                  </Box>
                  <Box className="lineman-form-dialog__shift-text">
                    <Typography component="span" className="shift-name">
                      {item.shiftName}
                    </Typography>
                    <Typography component="span" className="shift-time">
                      ({item.startTime} - {item.endTime})
                    </Typography>
                  </Box>
                </Box>
              ))}
            </FormGroup>
            <FieldError message={showError("iv_shift")} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography sx={fieldLabelSx}>Phone No</Typography>
            <TextField
              fullWidth
              size="small"
              name="iv_phone"
              value={formik.values.iv_phone}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              inputProps={{ maxLength: 11 }}
              autoComplete="off"
              error={Boolean(showError("iv_phone"))}
              helperText={showError("iv_phone")}
              className="lineman-form-dialog__input"
              sx={editableInputSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography sx={fieldLabelSx}>Employee Code</Typography>
            <TextField
              fullWidth
              size="small"
              name="iv_employeeCode"
              value={formik.values.iv_employeeCode}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              inputProps={{ maxLength: 13 }}
              autoComplete="off"
              error={Boolean(showError("iv_employeeCode"))}
              helperText={showError("iv_employeeCode")}
              className="lineman-form-dialog__input"
              sx={editableInputSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography sx={fieldLabelSx}>CNIC</Typography>
            <TextField
              fullWidth
              size="small"
              name="iv_cnic"
              value={formik.values.iv_cnic}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              inputProps={{ maxLength: 13 }}
              autoComplete="off"
              error={Boolean(showError("iv_cnic"))}
              helperText={showError("iv_cnic")}
              className="lineman-form-dialog__input"
              sx={editableInputSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography sx={fieldLabelSx}>User Id.</Typography>
            <TextField
              fullWidth
              size="small"
              name="iv_userId"
              value={formik.values.iv_userId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              autoComplete="off"
              error={Boolean(showError("iv_userId"))}
              helperText={showError("iv_userId")}
              className="lineman-form-dialog__input"
              sx={editableInputSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography sx={fieldLabelSx}>Password</Typography>
            <TextField
              fullWidth
              size="small"
              name="iv_password"
              type={showPassword ? "text" : "password"}
              value={formik.values.iv_password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              autoComplete="off"
              inputProps={{ maxLength: 16 }}
              error={Boolean(showError("iv_password"))}
              className="lineman-form-dialog__input"
              sx={editableInputSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={onTogglePassword}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {showError("iv_password") ? (
              <Box
                component="p"
                sx={{ color: "#dc2626", fontSize: "0.75rem", mt: 0.5, mb: 0 }}
              >
                Password should:
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  <li>have length between 8 to 16 characters.</li>
                  <li>contain at least 1 lower case letter.</li>
                  <li>contain at least 1 upper case letter.</li>
                  <li>contain at least 1 number.</li>
                  <li>contain at least 1 special character.</li>
                </Box>
              </Box>
            ) : null}
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography sx={fieldLabelSx}>Password Confirmation</Typography>
            <TextField
              fullWidth
              size="small"
              name="iv_confirm_password"
              type={showConfirmPassword ? "text" : "password"}
              value={formik.values.iv_confirm_password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              autoComplete="off"
              inputProps={{ maxLength: 16 }}
              error={Boolean(showError("iv_confirm_password"))}
              helperText={showError("iv_confirm_password")}
              className="lineman-form-dialog__input"
              sx={editableInputSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={onToggleConfirmPassword}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                      size="small"
                    >
                      {showConfirmPassword ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions className="lineman-form-dialog__actions">
        <Button
          type="button"
          variant="outlined"
          onClick={onClose}
          className="lineman-form-dialog__btn-cancel"
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="contained"
          onClick={formik.handleSubmit}
          className="lineman-form-dialog__btn-submit"
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AddWorkforceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  formik: PropTypes.object.isRequired,
  apiError: PropTypes.string,
  code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hierarchy: PropTypes.number,
  selectedCompanyCode: PropTypes.string,
  onCompanyChange: PropTypes.func.isRequired,
  circleCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  circleCodee: PropTypes.string,
  onCircleChange: PropTypes.func.isRequired,
  selectedDivision: PropTypes.string,
  onDivisionChange: PropTypes.func.isRequired,
  filteredCircles: PropTypes.arrayOf(PropTypes.object).isRequired,
  filteredDivisions: PropTypes.arrayOf(PropTypes.object).isRequired,
  filteredSubDivisions: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSubDivisionChange: PropTypes.func.isRequired,
  designations: PropTypes.arrayOf(PropTypes.object).isRequired,
  shifts: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedShiftIds: PropTypes.array.isRequired,
  onShiftToggle: PropTypes.func.isRequired,
  showPassword: PropTypes.bool.isRequired,
  showConfirmPassword: PropTypes.bool.isRequired,
  onTogglePassword: PropTypes.func.isRequired,
  onToggleConfirmPassword: PropTypes.func.isRequired,
};
